import axios, { AxiosResponse } from 'axios';
import { Issuer } from './center';
import { createDataObject } from '../lib/utils';
import { SalesManager, SalesStatus } from '../services/useMembershipPaymentsQueryService';
import {
    MembershipTemplateSharePolicy,
    ShareProvisioningView,
} from '../features/sharedMembership/types';

export /**
 * @method get
 * @brief 수강권 상품 조회 api
 */

const getMembershipTemplatesListAPI = async (accessToken: string, centerId: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipTemplates?centerId=${centerId}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 수강권 상품 1개 detail 조회 api
 */
const getDetailMembershipAPI = async (membershipTemplateId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipTemplates/${membershipTemplateId}`,
            headers: { authorization: accessToken },
            data: {
                membershipTemplateId,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 수강권 생성 api
 */
const createMembershipTemplatesAPI = async (
    accessToken: string,
    name: string,
    centerId: string,
    period: string,
    pricePerSchedule: number,
    totalPrice: number,
    totalSession: string,
    BGColor: any,
    memo: string,
    validDays: string,
    isReRegistrationMetricEligible: boolean = true,
    sharePolicy?: MembershipTemplateSharePolicy,
    /**
     * 정산 분류. undefined 면 키를 아예 보내지 않는다(구서버는 이 필드를 400 으로 거부한다).
     * 호출부는 값이 정해졌을 때만 넘긴다. null = 미분류.
     */
    settlementCategoryKey?: string | null,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipTemplates`,
            headers: { authorization: accessToken },
            data: {
                name,
                centerId,
                period,
                pricePerSchedule,
                totalPrice,
                totalSession,
                BGColor,
                memo,
                validDays,
                isReRegistrationMetricEligible,
                ...(sharePolicy !== undefined ? { sharePolicy } : {}),
                ...(settlementCategoryKey !== undefined ? { settlementCategoryKey } : {}),
            },
        });

        return response;
    } catch (error: any) {
        return error;
    }
};

export /**
 * @method patch
 * @brief 수강권 상품 수정
 */
const updateMembershipTemplatesAPI = async (
    accessToken: string,
    membershipTemplateId: string,
    name: string,
    period: string,
    pricePerSchedule: number,
    totalPrice: number,
    totalSession: string,
    BGColor: any,
    memo: string,
    validDays: string,
    isAvailable: boolean,
    isReRegistrationMetricEligible?: boolean,
    sharePolicy?: MembershipTemplateSharePolicy,
    /** 정산 분류. 바뀌었을 때만 넘긴다(undefined = 키 생략, 구서버 400 회피). null = 미분류. */
    settlementCategoryKey?: string | null,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipTemplates/${membershipTemplateId}`,
            headers: { authorization: accessToken },
            data: {
                name,
                period,
                pricePerSchedule,
                totalPrice,
                totalSession,
                BGColor,
                memo,
                validDays,
                isAvailable,
                isReRegistrationMetricEligible,
                ...(sharePolicy !== undefined ? { sharePolicy } : {}),
                ...(settlementCategoryKey !== undefined ? { settlementCategoryKey } : {}),
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        return error;
    }
};

export /**
 * @method patch
 * @brief 수강권 상품 표시 순서 일괄 변경 api (센터 관리자 전용)
 * 배열 순서가 곧 표시 순서다. 관리자 목록에 보이는 상품 전체(활성·비활성·강사 생성 포함)를 보낸다.
 * 다른 센터 상품이 섞이면 404, 관리자가 아니면 406.
 */

const reorderMembershipTemplatesAPI = async (
    accessToken: string,
    membershipTemplateIds: string[],
): Promise<{ status: number; data?: { membershipTemplateIds: string[]; updatedCount: number } }> => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipTemplates/reorder`,
            headers: { authorization: accessToken },
            data: { membershipTemplateIds },
        });
        return { status: response.status, data: response.data };
    } catch (error: any) {
        return { status: error?.response?.status ?? 500 };
    }
};

export /**
 * @method get
 * @brief 회원의 회원권 모두 가져오기 V2
 */
const getAllMembershipForMember = async (coachId: string, memberId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v2/memberships/coach/${coachId}/member/${memberId}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export type MembershipStartTodayResult = {
    status?: number;
    data?: {
        membership?: { activationDate: string };
        changed?: boolean;
        reason?: string;
        code?: string | number;
        errorCode?: string;
        message?: string;
    };
};

/** 시작일 기대값은 화면이 받은 원본 그대로 보낸다. 네트워크 실패도 결과 미확정으로 전달한다. */
export const startMembershipToday = async ({
    membershipId,
    expectedActivationDate,
    expectedExpirationDate,
    accessToken,
}: {
    membershipId: string;
    expectedActivationDate: string;
    expectedExpirationDate: string;
    accessToken: string;
}): Promise<MembershipStartTodayResult> => {
    try {
        return await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v3/memberships/${membershipId}/start-today`,
            headers: { authorization: accessToken },
            data: { expectedActivationDate, expectedExpirationDate },
        });
    } catch (error: any) {
        return { status: error?.response?.status, data: error?.response?.data };
    }
};

export /**
 * @method get
 * @brief 회원의 회원권 모두 가져오기 V2
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/Membership.md#%ED%9A%8C%EC%9B%90%EA%B6%8C-%EC%A1%B0%ED%9A%8C-v2
 */
/**
 * 수강권 단건 조회
 * - source === 'group'이면 MemberTicket을 Membership-compat shape로 변환해 반환하는
 *   서버 엔드포인트 `/v1/centers/:centerId/member-tickets/:id/detail`을 호출한다. (커밋 6bf11df5)
 * - source 미지정(= 'pt')이면 기존 PT 엔드포인트 유지.
 */
const getMembershipForMember = async (
    membershipId: string,
    accessToken: string,
    source?: 'pt' | 'group',
    centerId?: string,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    const url =
        source === 'group' && centerId
            ? `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${membershipId}/detail`
            : `${process.env.REACT_APP_SERVER}/v2/memberships/${membershipId}`;
    try {
        response = await axios({
            method: 'get',
            url,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 *
 * @brief 회원권 생성
 */
const createMemberShip = async ({
    coachId,
    memberId,
    membershipTemplateId,
    name,
    period,
    activationDate,
    expirationDate,
    startSessionValue,
    totalSession,
    memo,
    pricePerSchedule,
    status,
    totalPrice,
    issuer,
    BGColor,
    salesManager,
    isNewSales,
    initialPaymentDate,
    isSplitPayment,
    totalPaymentAmount,
    paymentMethod,
    rappoPayBillingMode,
    expiryPolicy,
    accessToken,
}: MemberShipApiType & { accessToken: string }): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v3/memberships`,
            headers: {
                authorization: accessToken,
            },
            data: {
                coachId,
                memberId,
                membershipTemplateId,
                name,
                period,
                activationDate,
                expirationDate,
                startSessionValue,
                totalSession,
                memo,
                pricePerSchedule,
                status,
                totalPrice,
                issuer,
                BGColor,
                salesManager,
                isNewSales,
                initialPaymentDate,
                isSplitPayment,
                totalPaymentAmount,
                // TODO: 백엔드 수강권 생성 API에서 필드명·스펙 확정 후 정리
                paymentMethod,
                rappoPayBillingMode,
                expiryPolicy,
            },
        });

        if (response.status === 201) {
            return response;
        }

        if (response.status === 209) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 공유 상품 발급 실패는 409(SHARE_ISSUANCE_IDENTITY_INVALID 등)로 온다. 분기되지 않은
        // 상태코드를 undefined로 삼키면 호출부의 성공/실패 콜백이 모두 건너뛰어져 화면이
        // 무음 실패한다. 항상 상태와 서버 본문(errorCode/message)을 그대로 넘긴다.
        const status = error?.response?.status ?? 0;
        const data = error?.response?.data;
        switch (status) {
            case 401:
                return { status, data };
            case 403:
                return { status, data };
            case 404:
                return { status, data };
            case 409:
                return { status, data };
            case 450:
                return { status, data };
            case 500:
                return { status, data };
            default:
                return { status, data };
        }
    }
};
export /**
 *
 * @brief 회원권 수정
 */
const updateMemberShip = async ({
    membershipId,
    name,
    period,
    activationDate,
    expirationDate,
    startSessionValue,
    totalSession,
    memo,
    status,
    pricePerSchedule,
    totalPrice,
    salesManager,
    initialPaymentDate,
    isNewSales,
    isSplitPayment,
    source,
    centerId,
    remainingCount,
    usedCount,
    expiryPolicy,
    reRegistrationEndReason,
    reRegistrationEndedAt,
    accessToken,
}: UpdateMemberShipApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    // 그룹 수강권(MemberTicket): PUT /v1/centers/:centerId/member-tickets/:id
    // PT 필드 → MemberTicket 필드 매핑 (activationDate→startDate, expirationDate→expiryDate 등)
    if (source === 'group' && centerId) {
        const groupStatus =
            status === 'active' ? 'ACTIVE' : status === 'expired' ? 'EXPIRED' : undefined;
        const groupBody = await createDataObject({
            status: groupStatus,
            memo,
            isSplitPayment,
            isNewSales,
            totalPrice,
            perSessionFee: pricePerSchedule,
            salesManager,
            totalSession,
            startDate: activationDate,
            expiryDate: expirationDate,
            paymentDate: initialPaymentDate,
            startSessionValue,
            remainingCount,
            usedCount,
            expiryPolicy,
        });
        try {
            response = await axios({
                method: 'put',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${membershipId}`,
                headers: { authorization: accessToken },
                data: groupBody,
            });
            if (response.status === 200) return response;
        } catch (error: any) {
            // 미리 나열하지 않은 상태코드(412, 500 등)도 호출부가 실패를 인지할 수 있게 그대로 돌려준다.
            //   안내 문구는 호출부(onUpdatedMemberShipFail)가 모달로 띄운다.
            const status = error.response?.status;
            const data = error.response?.data;
            return data === undefined ? { status } : { status, data };
        }
        return response;
    }

    const data = await createDataObject({
        name,
        period,
        activationDate,
        expirationDate,
        startSessionValue,
        totalSession,
        memo,
        status,
        pricePerSchedule,
        salesManager,
        totalPrice,
        initialPaymentDate,
        isSplitPayment,
        isNewSales,
        expiryPolicy,
        reRegistrationEndReason,
        reRegistrationEndedAt,
    });

    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v3/memberships/${membershipId}`,
            headers: {
                authorization: accessToken,
            },
            data: data,
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 400(요청 값 검증 실패)·412(수강권 수정 권한 OFF)처럼 나열되지 않은 상태코드까지 반드시 돌려준다.
        //   예전엔 여기서 undefined 가 반환돼 호출부 성공 콜백이 터지고 화면엔 아무 반응도 남지 않았다.
        //   안내 문구는 호출부(onUpdatedMemberShipFail)가 모달로 띄운다.
        const status = error.response?.status;
        const data = error.response?.data;
        return data === undefined ? { status } : { status, data };
    }

    return response;
};
export /**
 *
 * @brief 회원권 수정
 */
const deleteMemberShip = async (
    membershipId: string,
    accessToken: string,
    source?: 'pt' | 'group',
    centerId?: string,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    const url =
        source === 'group' && centerId
            ? `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${membershipId}`
            : `${process.env.REACT_APP_SERVER}/v2/memberships/${membershipId}`;

    try {
        response = await axios({
            method: 'delete',
            url,
            headers: {
                authorization: accessToken,
            },
        });

        // PT: 204, 그룹: 200 (soft delete 성공 시)
        if (response.status === 204 || response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 409:
                // 그룹: 연결된 booking이 있으면 409 → ModalDeleteError 표시
                return { status: error.response.status, data: error.response.data };
            case 450:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
    }
};

export /**
 *
 * @brief 수강권 상품 이전
 */
const patchRelocateMembership = async ({
    newMembershipTemplateId,
    membershipIds,
    accessToken,
}: PatchRelocateMembershipApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipTemplates/relocate/memberships`,
            headers: {
                authorization: accessToken,
            },
            data: {
                newMembershipTemplateId,
                membershipIds,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 450:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
    }
};
export /**
 *
 * @brief 수강권 상품 삭제
 */
const deleteMembershipTemplate = async ({
    membershipTemplateId,
    accessToken,
}: {
    membershipTemplateId: string;
    accessToken: string;
}): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipTemplates/${membershipTemplateId}`,
            headers: {
                authorization: accessToken,
            },
        });

        if (response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 450:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
    }
};

export /**
 *
 * @brief 수강권 신규 / 재등록 여부 조회
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/Membership.md#%EC%88%98%EA%B0%95%EA%B6%8C-%EC%8B%A0%EA%B7%9C--%EC%9E%AC%EB%93%B1%EB%A1%9D-%EC%97%AC%EB%B6%80-%EC%A1%B0%ED%9A%8C
 */
const getIsNewSales = async (coachId: string, memberId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/memberships/isNewSales/${coachId}/${memberId}`,
            headers: {
                authorization: accessToken,
            },
        });
        if (response.status === 200 || response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                return null;
        }
    }
};
export /**
 *
 * @brief 수강권 월별로 커미션 커스텀
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1197/src/docs/Membership.md#%EC%88%98%EA%B0%95%EA%B6%8C-%EC%9B%94%EB%B3%84%EB%A1%9C-%EC%BB%A4%EB%AF%B8%EC%85%98-%EC%BB%A4%EC%8A%A4%ED%85%80
 */
const patchMonthCommission = async ({
    membershipId,
    year,
    month,
    commissionType,
    commission,
    accessToken,
}: PatchMonthCommissionApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/memberships/${membershipId}/custom-commission`,
            headers: {
                authorization: accessToken,
            },
            data: {
                year,
                month,
                commissionType,
                commission,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 450:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
    }
};

export /**
 *
 * @brief MemberTicket(그룹 수강권) 월별로 커미션 커스텀
 * 서버 커밋 99a96b2c (feature/public-member branch)
 * PT의 patchMonthCommission과 동일 패턴. 그룹 row의 membershipId에 들어있는 MemberTicket ID로 호출.
 */
const patchMemberTicketMonthCommission = async ({
    centerId,
    memberTicketId,
    year,
    month,
    commissionType,
    commission,
    accessToken,
}: PatchMemberTicketMonthCommissionApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${memberTicketId}/custom-commission`,
            headers: {
                authorization: accessToken,
            },
            data: {
                year,
                month,
                commissionType,
                commission,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 450:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
    }
};

export /**
 *
 * @brief 수강권 월별 커스텀 커미션 해제 (PT)
 * PLAN_TIER_CUSTOM_COMMISSION §5.6 (D5). patchMonthCommission의 대칭 API.
 * 전달한 year/month **이하의 모든** 커스텀 설정을 제거한다. 해당 월 것만 지우면
 * fallbackToPrevious 조회로 이전 달 커스텀이 되살아나 기본 요율로 돌아가지 않는다.
 * axios delete는 body를 config.data로 실어야 한다.
 */
const deleteMonthCommission = async ({
    membershipId,
    year,
    month,
    accessToken,
}: DeleteMonthCommissionApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/memberships/${membershipId}/custom-commission`,
            headers: {
                authorization: accessToken,
            },
            data: {
                year,
                month,
            },
        });

        // 서버가 200(변경 문서 반환) / 204(no content) 중 무엇을 쓰든 성공으로 본다
        if (response.status === 200 || response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response?.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 450:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
        return { status: error.response?.status };
    }
};

export /**
 *
 * @brief MemberTicket(그룹 수강권) 월별 커스텀 커미션 해제
 * PLAN_TIER_CUSTOM_COMMISSION §5.6 (D5). patchMemberTicketMonthCommission의 대칭 API.
 * PT와 동일하게 year/month 이하의 모든 커스텀 설정을 제거한다.
 */
const deleteMemberTicketMonthCommission = async ({
    centerId,
    memberTicketId,
    year,
    month,
    accessToken,
}: DeleteMemberTicketMonthCommissionApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${memberTicketId}/custom-commission`,
            headers: {
                authorization: accessToken,
            },
            data: {
                year,
                month,
            },
        });

        if (response.status === 200 || response.status === 204) {
            return response;
        }
    } catch (error: any) {
        switch (error.response?.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 450:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
        return { status: error.response?.status };
    }
};

export /**
 *
 * @brief 센터별 미납 수강권 조회
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1204/src/docs/Membership.md#%EC%84%BC%ED%84%B0%EB%B3%84-%EB%AF%B8%EB%82%A9-%EC%88%98%EA%B0%95%EA%B6%8C-%EC%A1%B0%ED%9A%8C
 */
const getUnpaidPayments = async ({ userId, role, centerId, accessToken }: GetUnpaidPaymentsApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/memberships/center/${centerId}/unpaid-payments?userId=${userId}&role=${role}`,
            headers: {
                authorization: accessToken,
            },
        });
        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        if (error?.response?.status === 503) {
            return { status: 503, data: error.response.data };
        }
        switch (error.response.status) {
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 412: // 수강권 권한 없음
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return null;
        }
    }
};

export /** PT 수강권 → 그룹수업 수강권 전환 */
const convertMembershipToGroupTicket = async ({
    centerId,
    membershipId,
    ticketId,
    startDate,
    memo,
    accessToken,
}: {
    centerId: string;
    membershipId: string;
    ticketId: string;
    startDate?: string;
    memo?: string;
    accessToken: string;
}): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/memberships/${membershipId}/convert-to-group-ticket`,
            headers: { authorization: accessToken },
            data: { ticketId, startDate, memo },
        });
        if (response.status === 201) {
            return response;
        }
    } catch (error: any) {
        switch (error.response?.status) {
            case 409:
                return { status: error.response.status, message: error.response.data?.message };
            case 404:
                return { status: error.response.status, message: error.response.data?.message };
            case 412:
                return { status: error.response.status, message: error.response.data?.message };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
            default:
                return { status: error.response?.status || 500, message: error.response?.data?.message };
        }
    }
};

/** 수강권 등록 시 결제 방식 (백엔드 스펙 확정 전 프론트 임시 필드) */
export type MembershipPaymentMethod = 'inPerson' | 'rappoPay';

export type MembershipCreationResult = {
    status?: number;
    data?: {
        membership?: {
            _id?: string;
            id?: string;
        };
        shareProvisioning?: ShareProvisioningView;
    };
};

/** 라포페이 결제요청 시 선결제 / 수업당 결제 구분 (백엔드 스펙 확정 전 프론트 필드) */
export type RappoPayBillingMode = 'prepaid' | 'perClass';

export type MemberShipApiType = {
    coachId?: string;
    memberId?: string;
    membershipTemplateId: string;
    name: string;
    period: number;
    activationDate: string;
    expirationDate: string;
    startSessionValue: number;
    totalSession: number;
    memo: string;
    pricePerSchedule: number;
    status: 'active' | 'expired';
    totalPrice: number;
    currentSession?: number;
    remainSession?: number;
    BGColor: string[];
    issuer: {
        issuerId?: string;
        issuerRole?: 'admin' | 'coach';
    };
    isNewSales: boolean;
    initialPaymentDate: string;
    salesManager: SalesManager;
    isSplitPayment?: boolean;
    totalPaymentAmount?: number;
    /** 결제 방식 — 백엔드 연동 전까지 optional, API body에 포함(서버 미지원 시 무시 가능) */
    paymentMethod?: MembershipPaymentMethod;
    /** 라포페이 결제요청 시에만 의미 있음: 즉시 선결제 vs 수업당 결제 */
    rappoPayBillingMode?: RappoPayBillingMode;
    /** 만료 정책 — 미전달 시 'COUNT' (레거시 기본값) */
    expiryPolicy?: 'COUNT' | 'PERIOD' | 'FIRST';
};

export type UpdateMemberShipApiType = {
    membershipId: string;
    name?: string;
    period?: number;
    activationDate?: string;
    expirationDate?: string;
    startSessionValue?: number;
    totalSession?: number;
    memo?: string;
    status?: 'active' | 'expired';
    pricePerSchedule?: number;
    totalPrice?: number;
    isNewSales?: boolean;
    initialPaymentDate?: string;
    salesManager?: SalesManager;
    isSplitPayment?: boolean;
    accessToken: string;
    /** 'group'이면 MemberTicket 엔드포인트(PUT /v1/centers/:centerId/member-tickets/:id)로 분기 */
    source?: 'pt' | 'group';
    /** source='group'일 때 필요 */
    centerId?: string;
    /** 그룹 수강권 수정 시 잔여 회차 직접 지정 (PT의 totalSession 대신) */
    remainingCount?: number;
    /** 그룹 수강권 수정 시 사용 회차 직접 지정 */
    usedCount?: number;
    /** 그룹 수강권 수정 시 만료 정책 override */
    expiryPolicy?: 'COUNT' | 'PERIOD' | 'FIRST';
    /** PT 수강권을 정상 만료할 때 재등록 성공률의 종료 코호트로 기록 */
    reRegistrationEndReason?: 'MANUAL_NORMAL';
    /** 수동 정상 만료 시점. 미전달 시 서버 요청 처리 시각을 사용 */
    reRegistrationEndedAt?: string;
};
export type PatchRelocateMembershipApiType = {
    newMembershipTemplateId: string;
    membershipIds: string[];
    accessToken: string;
};

export type PatchMonthCommissionApiType = {
    membershipId: string;
    year: string;
    month: string;
    commissionType: 'percentage' | 'fixed';
    commission: number;
    accessToken: string;
};

export type PatchMemberTicketMonthCommissionApiType = {
    centerId: string;
    memberTicketId: string;
    year: string;
    month: string;
    commissionType: 'percentage' | 'fixed';
    commission: number;
    accessToken: string;
};

/** 커스텀 커미션 해제 (PT). 요율 값은 보내지 않는다 — 해당 년월 항목 제거만 한다. */
export type DeleteMonthCommissionApiType = {
    membershipId: string;
    year: string;
    month: string;
    accessToken: string;
};

/** 커스텀 커미션 해제 (그룹 수강권). */
export type DeleteMemberTicketMonthCommissionApiType = {
    centerId: string;
    memberTicketId: string;
    year: string;
    month: string;
    accessToken: string;
};

export type GetUnpaidPaymentsApiType = {
    userId: string;
    role: string;
    centerId: string;
    accessToken: string;
};
